import Script from 'next/script'
import { useCallback, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import cn from 'clsx'
import { getCybersourceCaptureContext } from '@lib/api/cybersource/getCybersourceCaptureContext'
import CVVIcon from '@components/icons/CVVIcon'
import MasterCardIcon from '@components/icons/MasterCardIcon'
import VisaIcon from '@components/icons/VisaIcon'
import { StandardButton } from '@components/ui/StandardButton'
import { Loader } from '@components/icons'
import { FormDropdown } from '@components/ui/Form/FormDropdown'
import { FormTextInput } from '@components/ui/Form/FormTextInput'

export enum CybersourceErrors {
  InvalidCardNumber = 'number',
  InvalidCardCvc = 'securityCode',
}

export interface PayingFormData {
  cardHolderName: string
  cardNumber: string
  expirationMonth: string
  expirationYear: string
  cvv: string
}
export interface PayingFormError<T = PayingFormData> {
  field: keyof T
  messageKey?: string
}
import { AmexIcon } from '@components/icons/AmexIcon'
import { ThreeDSecureIcon } from '@components/icons/ThreeDSecureIcon'
import { sendEvent } from '@lib/tracking'

export const FiatPayment = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [captureContext, setCaptureContext] = useState<string | null>(null)
  const [microform, setMicroform] = useState<FlexMicroformResult | null>(null)
  const [isCardNumberFocused, setIsCardNumberFocused] = useState(false)
  const [isCardNumberEmpty, setIsCardNumberEmpty] = useState(true)
  const [numberField, setNumberField] = useState<MicroformField | null>(null)
  const [isSecurityCodeFocused, setIsSecurityCodeFocused] = useState(false)
  const [isSecurityCodeEmpty, setIsSecurityCodeEmpty] = useState(true)
  const [securityCodeField, setSecurityCodeField] =
    useState<MicroformField | null>(null)

  const myStyles = {
    input: {
      'font-size': '14px',
      'font-family': 'tahoma',
      color: 'white',
    },
  }

  const initFlex = async () => {
    const context = await getCybersourceCaptureContext()

    setCaptureContext(context.getCybersourceCaptureContext ?? null)

    const flex = new window.Flex(context.getCybersourceCaptureContext ?? null)
    const _microform = flex.microform({ styles: myStyles })

    const number: MicroformField = _microform.createField('number')

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    number.on('focus', () => {
      setIsCardNumberFocused(true)
    })

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    number.on('change', (event: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setIsCardNumberEmpty(event.empty)
    })

    number.on('blur', () => {
      if (isCardNumberEmpty) {
        setIsCardNumberFocused(false)
      }
    })

    const securityCode = _microform.createField('securityCode')

    securityCode.on('focus', () => {
      setIsSecurityCodeFocused(true)
    })

    securityCode.on('change', (event: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setIsSecurityCodeEmpty(event.empty)
    })

    securityCode.on('blur', () => {
      if (isSecurityCodeEmpty) {
        setIsSecurityCodeFocused(false)
      }
    })

    number.load('#number-container')
    securityCode.load('#securityCode-container')

    setMicroform(_microform)
    setNumberField(number)
    setSecurityCodeField(securityCode)
  }

  const handleFlexLoaded = () => {
    initFlex().catch(console.error)
  }

  const schema = yup
    .object({
      cardHolderName: yup
        .string()
        .required(
          t('checkout:cardholder_field') + ' ' + t('checkout:is_required')
        ),
      expirationMonth: yup
        .string()
        .required(
          t('checkout:expiry_date_field') + ' ' + t('checkout:is_required')
        ),
      expirationYear: yup
        .string()
        .required(
          t('checkout:expiry_date_field') + ' ' + t('checkout:is_required')
        ),
    })
    .required(t('checkout:missing_fields'))

  const {
    handleSubmit: handlePayingSubmit,
    control: payingControl,
    clearErrors: clearPayingErrors,
    formState: { errors: payingErrors },
    setError: setPayingError,
  } = useForm<PayingFormData>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })

  const setPayingFormErrors = useCallback(
    (formErrors: PayingFormError[]) => {
      formErrors.forEach(({ field, messageKey }) => {
        setPayingError(`${field}`, {
          type: 'custom',
          message: t(messageKey ?? 'checkout:invalid_form_field', { field }),
          types: { value: true, validate: true },
        })
      })
    },
    [setPayingError, t]
  )

  const submitPayment = useCallback(
    (payingData: {
      cardHolderName: string
      expirationMonth: string
      expirationYear: string
    }) => {
      setLoading(true)
      clearPayingErrors()

      const form = document.querySelector('#my-sample-form') as HTMLFormElement

      const options: { expirationMonth: unknown; expirationYear: unknown } = {
        expirationMonth: payingData.expirationMonth,
        expirationYear: payingData.expirationYear,
      }

      if (microform !== null) {
        const expiryDate = new Date(
          Number(payingData.expirationYear),
          Number(payingData.expirationMonth),
          0
        )
        if (expiryDate <= new Date()) {
          setPayingFormErrors([
            {
              field: 'expirationMonth',
              messageKey: t('checkout:invalid_expiry_date_field'),
            },
          ])
          setLoading(false)

          return
        }

        microform.createToken(
          options,
          function (
            err: { details: { location: string }[] } | undefined,
            token: string
          ) {
            if (err) {
              // handle error
              err.details.forEach((detail) => {
                if (detail.location === CybersourceErrors.InvalidCardNumber) {
                  setPayingFormErrors([
                    {
                      field: 'cardNumber',
                      messageKey: t('checkout:invalid_card_number_field'),
                    },
                  ])
                } else if (
                  detail.location === CybersourceErrors.InvalidCardCvc
                ) {
                  setPayingFormErrors([
                    {
                      field: 'cvv',
                      messageKey: t('checkout:invalid_cvv_field'),
                    },
                  ])
                }
              })

              setLoading(false)
            } else {
              console.log(JSON.stringify(token))
              // @ts-expect-error TODO
              document.getElementById('transient_token').value = token
              // @ts-expect-error TODO
              document.getElementById('capture_context').value = captureContext
              form.submit()
            }
          }
        )
      }
    },
    [captureContext, clearPayingErrors, microform, setPayingFormErrors, t]
  )

  const months = [
    { label: '01', value: '01' },
    { label: '02', value: '02' },
    { label: '03', value: '03' },
    { label: '04', value: '04' },
    { label: '05', value: '05' },
    { label: '06', value: '06' },
    { label: '07', value: '07' },
    { label: '08', value: '08' },
    { label: '09', value: '09' },
    { label: '10', value: '10' },
    { label: '11', value: '11' },
    { label: '12', value: '12' },
  ]

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 11 }, (_, i) => currentYear + i)

  const onSecurityCodeClick = useCallback(() => {
    if (securityCodeField !== null) {
      securityCodeField.focus()
    }
  }, [securityCodeField])

  const onCardNumberClick = useCallback(() => {
    if (numberField !== null) {
      numberField.focus()
    }
  }, [numberField])

  return (
    <div className="mt-6">
      <Script
        onReady={handleFlexLoaded}
        src="https://flex.cybersource.com/cybersource/assets/microform/2/flex-microform.min.js"
      />
      <form
        action={process.env.NEXT_PUBLIC_CYBERSOURCE_ENDPOINT}
        id="my-sample-form"
        method="post"
        className="pb-7"
        onSubmit={useCallback(
          async (event: React.FormEvent) => {
            sendEvent({
              event: 'Submit_Fiat_Payment',
              data: {},
            })
            event.preventDefault()
            await handlePayingSubmit((payingData) => {
              return submitPayment(payingData)
            })(event)
          },
          [handlePayingSubmit, submitPayment]
        )}
      >
        {Object.values(payingErrors).length > 0 &&
        Object.values(payingErrors)[0].message !== undefined ? (
          <p className="text-error">
            <span className="font-bold">{t('checkout:error')}</span>{' '}
            {Object.values(payingErrors)[0].message}
          </p>
        ) : null}
        <div className="mb-5 mt-5">
          <Controller
            control={payingControl}
            name="cardHolderName"
            render={({ field, fieldState: { error } }) => (
              <FormTextInput
                id="cardholderName"
                className="w-full border border-deepPurple peer outline-none h-16 px-4 bg-transparent"
                label={`${t('checkout:cardholder_field')}*`}
                error={error}
                {...field}
              />
            )}
          />
          <div className="mt-5">
            <FormTextInput
              label="Card Number*"
              id="number-container"
              value={isCardNumberFocused || !isCardNumberEmpty ? 'true' : ''}
              renderCustomInput={({ ref, className, id }) => {
                return <div ref={ref} className={className} id={id}></div>
              }}
              error={payingErrors.cardNumber}
              onLabelClick={onCardNumberClick}
            />
          </div>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-5 grid-flow-row-dense">
          <Controller
            control={payingControl}
            name="expirationMonth"
            render={({ field, fieldState: { error } }) => (
              <FormDropdown
                id="expirationMonth"
                onSelect={(_, month) => {
                  field.onChange({ target: { value: month } })
                }}
                selectedLabel={field.value}
                placeholder="Month"
                showPlaceholderOnSelect={false}
                items={months}
                error={error}
                {...field}
              />
            )}
          />

          <Controller
            control={payingControl}
            name="expirationYear"
            render={({ field, fieldState: { error } }) => (
              <FormDropdown
                id="expirationYear"
                onSelect={(_, year) => {
                  field.onChange({ target: { value: year } })
                }}
                selectedLabel={field.value}
                placeholder="Year"
                showPlaceholderOnSelect={false}
                items={years.map((value) => ({
                  label: value.toString(),
                  value: value.toString(),
                }))}
                error={error}
                {...field}
              />
            )}
          />
          <div className="col-span-2 sm:col-span-2">
            <FormTextInput
              label="Security Code*"
              id="securityCode-container"
              value={
                isSecurityCodeFocused || !isSecurityCodeEmpty ? 'true' : ''
              }
              renderCustomInput={({ ref, className, id }) => {
                return <div ref={ref} className={className} id={id}></div>
              }}
              error={payingErrors.cvv}
              onLabelClick={onSecurityCodeClick}
            />
          </div>
          <div className="flex items-center justify-center">
            <CVVIcon />
          </div>
        </div>
        <div className="mt-14 flex justify-end">
          <StandardButton
            type="submit"
            label={
              loading ? (
                <Loader className="m-auto" />
              ) : (
                t('checkout:submit_payment')
              )
            }
            disabled={loading}
            onClick={() => {}}
            className="w-full sm:w-[280px]"
          />
        </div>
        <div className="w-full flex justify-end mt-5">
          <div className="relative">
            <ThreeDSecureIcon className="absolute bottom-1 right-2" />
          </div>
          <MasterCardIcon className="mr-2" />
          <VisaIcon className="mr-2" />
          <AmexIcon />
        </div>
        <input
          type="hidden"
          id="capture_context"
          name="capture_context"
          value=""
        />
        <input
          type="hidden"
          id="transient_token"
          name="transient_token"
          value=""
        />
      </form>
    </div>
  )
}
